<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { stripInlineBase64Images } from 'dashboard/helper/editorHelper';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  messageSignature: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['updateSignature']);

const { t } = useI18n();
const { uiSettings } = useUISettings();
const layout = ref(
  uiSettings.value?.message_signature_layout === 'next_line'
    ? 'next_line'
    : 'same_line'
);
const signature = ref(props.messageSignature ?? '');
watch(
  () => props.messageSignature ?? '',
  newValue => {
    signature.value = newValue;
  }
);

const updateSignature = () => {
  const { sanitizedContent, hasInlineImages } = stripInlineBase64Images(
    signature.value || ''
  );
  signature.value = sanitizedContent.trim();
  if (hasInlineImages) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.INLINE_IMAGE_WARNING')
    );
  }
  emit('updateSignature', signature.value, layout.value);
};
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="updateSignature()">
    <WootMessageEditor
      id="message-signature-input"
      v-model="signature"
      class="message-editor h-[10rem] !px-3"
      is-format-mode
      :placeholder="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE.PLACEHOLDER')"
      channel-type="Context::MessageSignature"
      :enable-suggestions="false"
    />
    <label class="flex flex-col gap-2 text-sm text-n-slate-12">
      {{ t('PROFILE_SETTINGS.CONVERSATION_PREFERENCES.SIGNATURE_LAYOUT') }}
      <select
        v-model="layout"
        class="mb-0 w-full rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12"
      >
        <option value="same_line">
          {{ t('PROFILE_SETTINGS.CONVERSATION_PREFERENCES.SAME_LINE') }}
        </option>
        <option value="next_line">
          {{ t('PROFILE_SETTINGS.CONVERSATION_PREFERENCES.NEXT_LINE') }}
        </option>
      </select>
      <span class="text-xs text-n-slate-11">{{
        t('PROFILE_SETTINGS.CONVERSATION_PREFERENCES.SIGNATURE_HINT')
      }}</span>
    </label>
    <div>
      <NextButton
        type="submit"
        :label="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.BTN_TEXT')"
      />
    </div>
  </form>
</template>
